/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { supabase } from './supabase'
import Avatar from './Avatar'

const FAMILY_MEMBERS = [
  { email: 'kkdbailey@gmail.com', name: 'Kevin', color: '#e94560' },
  { email: 'moniquebailey1026@gmail.com', name: 'Monique', color: '#5856d6' },
  { email: 'natashabbhatt@gmail.com', name: 'Tasha', color: '#ff9f0a' },
  { email: 'karahauswirth@gmail.com', name: 'Kara', color: '#30d158' },
  { email: 'perniel.grace19@gmail.com', name: 'Grace', color: '#64d2ff' },
  { email: 'mtraetin@gmail.com', name: 'Traetin', color: '#bf5af2' },
]

function getMember(email) {
  const lower = email?.toLowerCase()
  return FAMILY_MEMBERS.find(m => m.email.toLowerCase() === lower) || { name: email?.split('@')[0] || 'Unknown', color: '#8e8e93' }
}

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts)) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function getGreeting(name) {
  const h = new Date().getHours()
  if (h < 12) return `Good morning, ${name} 🌅`
  if (h < 17) return `Good afternoon, ${name} ☀️`
  return `Good evening, ${name} 🌙`
}

function Widget({ title, emoji, onClick, children, actionLabel }) {
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '16px', marginBottom: '14px', boxShadow: '0 1px 4px var(--shadow)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <p style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.2px' }}>{emoji} {title}</p>
        {onClick && <button onClick={onClick} style={{ fontSize: '12px', color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{actionLabel || 'View →'}</button>}
      </div>
      {children}
    </div>
  )
}

export default function Dashboard({ onNavigate }) {
  const [userEmail, setUserEmail] = useState('local')
  const [userName, setUserName] = useState('there')
  const [transactions, setTransactions] = useState([])
  const [latestPost, setLatestPost] = useState(null)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [fitnessStreak, setFitnessStreak] = useState(0)
  const [weather, setWeather] = useState(null)
  const [weatherLoading, setWeatherLoading] = useState(true)

  useEffect(() => {
    getUser()
    fetchRecent()
    fetchSocialPreview()
    fetchUpcomingEvents()
    fetchFitnessStreak()
    fetchWeather()
  }, [])

  useEffect(() => {
    if (userEmail && userEmail !== 'local') {
      const member = getMember(userEmail)
      setUserName(member.name)
      supabase.from('messages').select('id').eq('recipient_email', userEmail).is('read_at', null)
        .then(({ data, error }) => { if (!error) setUnreadMessages(data?.length || 0) })
    }
  }, [userEmail])

  async function getUser() {
    try {
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith('CF_Authorization='))
      if (cookie) {
        const token = cookie.trim().replace('CF_Authorization=', '')
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        if (payload.email) { setUserEmail(payload.email); return }
      }
    } catch {}
    const cached = localStorage.getItem('bf_user_email')
    setUserEmail(cached || 'local')
  }

  async function fetchRecent() {
    const { data, error } = await supabase.from('Transactions').select('*').order('date', { ascending: false }).limit(5)
    if (!error) setTransactions(data || [])
  }

  async function fetchSocialPreview() {
    const { data, error } = await supabase.from('feed_posts').select('*').order('created_at', { ascending: false }).limit(1)
    if (!error && data?.length) setLatestPost(data[0])
  }

  async function fetchUpcomingEvents() {
    const today = new Date().toISOString().slice(0, 10)
    const { data, error } = await supabase.from('events').select('*').gte('date', today).order('date', { ascending: true }).limit(3)
    if (!error) setUpcomingEvents(data || [])
  }

  async function fetchFitnessStreak() {
    const { data, error } = await supabase.from('workout_logs').select('date').order('date', { ascending: false }).limit(30)
    if (!error && data?.length) {
      let streak = 0
      let check = new Date()
      check.setHours(0, 0, 0, 0)
      const dates = data.map(d => new Date(d.date).toDateString())
      while (dates.includes(check.toDateString())) {
        streak++
        check.setDate(check.getDate() - 1)
      }
      setFitnessStreak(streak)
    }
  }

  async function fetchWeather() {
    try {
      const res = await fetch('https://api.open-meteo.com/v1/forecast?latitude=42.4975&longitude=-94.1680&current=temperature_2m,weathercode,windspeed_10m&temperature_unit=fahrenheit&windspeed_unit=mph')
      const data = await res.json()
      const code = data.current.weathercode
      const temp = Math.round(data.current.temperature_2m)
      const wind = Math.round(data.current.windspeed_10m)
      setWeather({ temp, wind, icon: weatherIcon(code), desc: weatherDesc(code) })
    } catch { setWeather(null) }
    setWeatherLoading(false)
  }

  function weatherIcon(code) {
    if (code === 0) return '☀️'
    if (code <= 2) return '⛅️'
    if (code <= 3) return '☁️'
    if (code <= 48) return '🌫️'
    if (code <= 67) return '🌧️'
    if (code <= 77) return '❄️'
    if (code <= 82) return '🌧️'
    if (code <= 86) return '❄️'
    if (code <= 99) return '⛈️'
    return '🌤️'
  }

  function weatherDesc(code) {
    if (code === 0) return 'Clear skies'
    if (code <= 2) return 'Partly cloudy'
    if (code <= 3) return 'Overcast'
    if (code <= 48) return 'Foggy'
    if (code <= 57) return 'Drizzle'
    if (code <= 67) return 'Rainy'
    if (code <= 77) return 'Snow'
    if (code <= 82) return 'Rain showers'
    if (code <= 86) return 'Snow showers'
    if (code <= 99) return 'Thunderstorm'
    return 'Mixed'
  }

  function formatEventDate(dateStr) {
    const d = new Date(dateStr + 'T00:00:00')
    const today = new Date(); today.setHours(0,0,0,0)
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1)
    if (d.toDateString() === today.toDateString()) return 'Today'
    if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  const income = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0)
  const expenses = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0)
  const balance = income - expenses
  const member = getMember(latestPost?.user_email)

  const quickActions = [
    { label: '💰 Transaction', tab: 'budget' },
    { label: '📝 Post', tab: 'social' },
    { label: '📷 Photo', tab: 'photos' },
    { label: '📅 Event', tab: 'calendar' },
  ]

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif', maxWidth: '680px', margin: '0 auto', paddingBottom: '24px' }}>

      {/* Greeting + Weather — keeps gradient, always looks good */}
      <div style={{ background: 'linear-gradient(135deg, #e94560 0%, #c13652 100%)', borderRadius: '20px', padding: '20px', marginBottom: '14px', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: '-30px', right: '40px', width: '80px', height: '80px', background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />
        <p style={{ fontSize: '22px', fontWeight: '700', marginBottom: '4px', letterSpacing: '-0.4px' }}>{getGreeting(userName)}</p>
        <p style={{ fontSize: '13px', opacity: 0.85, marginBottom: '14px' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        {weatherLoading ? (
          <p style={{ fontSize: '13px', opacity: 0.7 }}>Loading weather...</p>
        ) : weather ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '10px 14px' }}>
            <span style={{ fontSize: '28px' }}>{weather.icon}</span>
            <div>
              <p style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1 }}>{weather.temp}°F</p>
              <p style={{ fontSize: '12px', opacity: 0.85 }}>{weather.desc} · {weather.wind} mph winds · Fort Dodge</p>
            </div>
          </div>
        ) : (
          <p style={{ fontSize: '13px', opacity: 0.7 }}>Weather unavailable</p>
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
        {quickActions.map(a => (
          <button key={a.tab} onClick={() => onNavigate?.(a.tab)} style={{ background: 'var(--bg-card)', border: 'none', borderRadius: '14px', padding: '14px 8px', cursor: 'pointer', boxShadow: '0 1px 4px var(--shadow)', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', WebkitTapHighlightColor: 'transparent' }}>
            <span style={{ fontSize: '22px' }}>{a.label.split(' ')[0]}</span>
            <span>{a.label.split(' ').slice(1).join(' ')}</span>
          </button>
        ))}
      </div>

      {/* Budget widget */}
      <Widget title="Budget" emoji="💵" onClick={() => onNavigate?.('budget')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
          {[{ label: 'Income', val: income, color: '#34c759' }, { label: 'Expenses', val: expenses, color: '#ff3b30' }, { label: 'Balance', val: balance, color: balance >= 0 ? '#007aff' : '#ff3b30' }].map(item => (
            <div key={item.label} style={{ background: 'var(--bg-tertiary)', borderRadius: '12px', padding: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>{item.label}</p>
              <p style={{ fontSize: '15px', fontWeight: '700', color: item.color }}>${item.val.toFixed(2)}</p>
            </div>
          ))}
        </div>
        {transactions.slice(0, 3).map(t => (
          <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid var(--border-light)' }}>
            <div>
              <p style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>{t.description}</p>
              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{t.category} · {t.date}</p>
            </div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: t.type === 'income' ? '#34c759' : '#ff3b30' }}>
              {t.type === 'income' ? '+' : '-'}${t.amount.toFixed(2)}
            </p>
          </div>
        ))}
      </Widget>

      {/* Social widget */}
      <Widget title="Social" emoji="💬" onClick={() => onNavigate?.('social')} actionLabel={unreadMessages > 0 ? `${unreadMessages} new →` : 'View →'}>
        {latestPost ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Avatar email={latestPost.user_email} size={32} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{member.name}</p>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{timeAgo(latestPost.created_at)}</p>
                {unreadMessages > 0 && <span style={{ background: '#e94560', color: 'white', borderRadius: '10px', fontSize: '10px', fontWeight: '700', padding: '1px 6px' }}>{unreadMessages} new</span>}
              </div>
              {latestPost.content && (
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {latestPost.content}
                </p>
              )}
              {!latestPost.content && latestPost.photo_url && <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>📷 Shared a photo</p>}
            </div>
            {latestPost.photo_url && <img src={latestPost.photo_url} alt="post" style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />}
          </div>
        ) : (
          <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>No posts yet — be the first to share something.</p>
        )}
      </Widget>

      {/* Calendar widget */}
      <Widget title="Upcoming" emoji="📅" onClick={() => onNavigate?.('calendar')}>
        {upcomingEvents.length === 0 ? (
          <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>No upcoming events.</p>
        ) : (
          upcomingEvents.map(event => (
            <div key={event.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderTop: '1px solid var(--border-light)' }}>
              <div style={{ background: 'var(--bg-tertiary)', borderRadius: '10px', padding: '6px 10px', textAlign: 'center', flexShrink: 0 }}>
                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: '600' }}>{formatEventDate(event.date)}</p>
              </div>
              <div>
                <p style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>{event.title}</p>
                {event.time && <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{event.time}</p>}
              </div>
            </div>
          ))
        )}
      </Widget>

      {/* Fitness streak widget */}
      <Widget title="Fitness" emoji="💪" onClick={() => onNavigate?.('fitness')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ background: fitnessStreak > 0 ? 'linear-gradient(135deg, #ff9500, #ff6b00)' : 'var(--bg-tertiary)', borderRadius: '16px', padding: '16px 20px', textAlign: 'center', flexShrink: 0 }}>
            <p style={{ fontSize: '28px', fontWeight: '800', color: fitnessStreak > 0 ? 'white' : 'var(--text-hint)', lineHeight: 1 }}>{fitnessStreak}</p>
            <p style={{ fontSize: '11px', color: fitnessStreak > 0 ? 'rgba(255,255,255,0.85)' : 'var(--text-hint)', fontWeight: '600', marginTop: '2px' }}>day streak</p>
          </div>
          <div>
            {fitnessStreak > 0 ? (
              <>
                <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>🔥 Keep it up!</p>
                <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>You've worked out {fitnessStreak} day{fitnessStreak !== 1 ? 's' : ''} in a row.</p>
              </>
            ) : (
              <>
                <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>Start your streak</p>
                <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Log a workout to begin a streak.</p>
              </>
            )}
          </div>
        </div>
      </Widget>

    </div>
  )
}
